import { Component, Input, OnInit } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';
import { DIMENSION_CONSTANTS } from '../../utils/Constants';
import { StorageService } from 'src/app/services/storage.service';
import { ModalHelperService } from 'src/app/services/modal-helper';
import { WindowtypeUpdatesService } from 'src/app/services/windowtype-updates-service';
import { CalculationAutoUpdateService } from 'src/app/services/calculation-auto-update.service';

@Component({
  selector: 'app-edit-window-details',
  templateUrl: './edit-window-details.component.html',
  styleUrls: ['./edit-window-details.component.scss'],
  standalone: false,
})
export class EditWindowDetailsComponent implements OnInit {
  @Input() selectedWindowType: any;
  @Input() isComparisonMode: boolean = false;

  dimensionForm = {
    dimA: null as number | null,
    dimB: null as number | null,
    dimC: null as number | null
  };

  showHeightC = false;
  isFormValid = false;
  dimBLabel = 'HEADER_HEIGHT_B';

  constructor(private modalController: ModalController,
    private navParams: NavParams,
    private storageService: StorageService,
    private modalHelper: ModalHelperService,
    private windowtypeUpdatesService: WindowtypeUpdatesService,
    private calcAutoUpdate: CalculationAutoUpdateService
  ) {
    this.selectedWindowType = this.navParams.get('selectedWindowType');
  }

  ngOnInit() {
    this.initializeForm();
  }

  async initializeForm() {
    // Load existing values if available
    const res: any = this.isComparisonMode
      ? await this.storageService.getComparedProject()
      : await this.storageService.getProject();
    const storedProject = JSON.parse(res);
    this.setDefaultDimensions(this.selectedWindowType?.id)
    if (storedProject && storedProject?.dimensions?.a) {
      this.dimensionForm.dimA = this.metersToMm(storedProject.dimensions?.a);
      this.dimensionForm.dimB = this.metersToMm(storedProject.dimensions?.b);
      if (storedProject.dimensions?.c)
        this.dimensionForm.dimC = this.metersToMm(storedProject.dimensions?.c);

      this.validateForm();
    }
    console.log(this.selectedWindowType)
  }

  private metersToMm(value: number | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    return Math.round(value * 1000);
  }

  private mmToMeters(value: number | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    return value / 1000;
  }

  onDimensionInput(field: 'dimA' | 'dimB' | 'dimC', event: any): void {
    const rawValue = event?.target?.value ?? event?.detail?.value ?? '';
    const digitsOnly = String(rawValue).replace(/\D/g, '');

    this.dimensionForm[field] = digitsOnly ? Number(digitsOnly) : null;
    this.validateForm();
  }

  validateForm(): void {
    // Check if required fields have values
    const hasDimA = this.dimensionForm.dimA !== null && this.dimensionForm.dimA !== undefined && this.dimensionForm.dimA > 0;
    const hasDimB = this.dimensionForm.dimB !== null && this.dimensionForm.dimB !== undefined && this.dimensionForm.dimB > 0;

    // If height C is visible, it must also be filled
    const hasDimC = !this.showHeightC || (this.dimensionForm.dimC !== null && this.dimensionForm.dimC !== undefined && this.dimensionForm.dimC > 0);

    this.isFormValid = hasDimA && hasDimB && hasDimC;
  }

  setDefaultDimensions(windowID: number): void {
    console.log(windowID)
    switch (windowID) {
      // Triple sash
      case 4:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_DOUB),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_B_NORM),
          dimC: 0
        };
        this.showHeightC = false;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      // Vertical sliding
      case 5:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_NORM),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_B_HALF),
          dimC: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_C_HALF)
        };
        this.showHeightC = true;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      // Horizontal sliding
      case 6:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_HALF),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_B),
          dimC: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_C_NORM)
        };
        this.showHeightC = true;
        this.dimBLabel = 'WIDTH_B'; // You'll need to create this translation key
        break;

      // Single sash toplight
      case 7:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_NORM),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_B_NORM),
          dimC: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_C_OHL)
        };
        this.showHeightC = true;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      // Double sash toplight
      case 8:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_NORM),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_B_NORM),
          dimC: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_C_OHL)
        };
        this.showHeightC = true;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      // Cross
      case 10:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_NORM),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_B_NORM),
          dimC: 0
        };
        this.showHeightC = false;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      // Double sash splitted
      case 11:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_NORM),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_B_SPLIT),
          dimC: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_C_SPLIT)
        };
        this.showHeightC = true;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      // Triple sash splitted
      case 12:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_DOUB),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_B_SPLIT),
          dimC: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_C_SPLIT)
        };
        this.showHeightC = true;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      case 13:
        this.dimensionForm = {
          dimA: this.metersToMm(1.53),
          dimB: this.metersToMm(1.48),
          dimC: 0
        };
        this.showHeightC = true;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      case 14:
        this.dimensionForm = {
          dimA: this.metersToMm(2.18),
          dimB: this.metersToMm(2.35),
          dimC: 0
        };
        this.showHeightC = true;
        this.dimBLabel = 'HEADER_HEIGHT_B';
        break;

      // Default for single sash, double sash, two parted, fixed light
      default:
        this.dimensionForm = {
          dimA: this.metersToMm(DIMENSION_CONSTANTS.WIDTH_A_NORM),
          dimB: this.metersToMm(DIMENSION_CONSTANTS.HEIGHT_B_NORM),
          dimC: 0
        };
        this.showHeightC = false;
        this.dimBLabel = 'HEADER_HEIGHT_B';
    }
  }
  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }

  async next() {
    if (this.isFormValid) {
      const updates = {
        "windowtype": this.selectedWindowType.id,
        "dimensions": {
          "a": this.mmToMeters(this.dimensionForm.dimA),
          "b": this.mmToMeters(this.dimensionForm.dimB),
          "c": this.showHeightC ? this.mmToMeters(this.dimensionForm.dimC) : 0
        }
      };

      // Save to appropriate storage based on mode
      if (this.isComparisonMode) {
        await this.storageService.updateComparedProject(updates);
      } else {
        await this.storageService.updateProject(updates);
      }

      console.log(`Saved to ${this.isComparisonMode ? 'compared' : 'main'} project:`, updates);
      await this.windowtypeUpdatesService.recalculateFrameUfValues(
        this.selectedWindowType.id,
        this.isComparisonMode
      );
      const res = this.isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();
      const project = res ? JSON.parse(res) : null;
      const glazing = project?.windowglas?.glazingCount || 3;

      await this.windowtypeUpdatesService.autoUpdateByBrandFrame(
        this.selectedWindowType.id,
        glazing,
        this.isComparisonMode
      );
      if (!this.isComparisonMode) {
        await this.calcAutoUpdate.triggerAutoUpdate('windowtype', this.isComparisonMode);
      }
      //close all modals
      await this.modalController.dismiss({
        selectedOption: this.selectedWindowType,
        dimensions: updates.dimensions
      }, 'confirm');

      // this.modalController.dismiss(null, 'confirm');
    }
  }

  dismiss() {
    this.modalController.dismiss(null, 'dismiss');
  }

}
